import { OptimizelyEvent } from './optimizely/events'
import { SnowplowEvent } from './snowplow/events'
import * as Optimizely from './optimizely/optimizely'
import * as Snowplow from './snowplow/snowplow'
import config from 'constants/config'

export enum Tracker {
  optimizely = 'optimizely',
  snowplow = 'snowplow',
}

const eventSubscribers: Record<string, Tracker[]> = {
  'add-to-cart-tour': [Tracker.optimizely],
  'add-to-cart-accommodation': [Tracker.optimizely],
  'add-to-cart': [Tracker.optimizely, Tracker.snowplow],
  'agent-insurance-upsell-event': [Tracker.snowplow],
  'cart-event': [Tracker.snowplow],
  'click-tpfm-carousel': [Tracker.optimizely],
  'click-tour-offer': [Tracker.optimizely],
  'client-event': [Tracker.snowplow],
  'experience-event': [Tracker.snowplow],
  'impression-event': [Tracker.snowplow],
  'log-auth': [Tracker.snowplow],
  'log-operational': [Tracker.snowplow],
  'log-registration': [Tracker.snowplow],
  'offer-click-accommodation': [Tracker.optimizely],
  'offer-event': [Tracker.snowplow],
  'offer-page-hot-lead-event': [Tracker.snowplow],
  'purchase-click': [Tracker.optimizely],
  'purchase-lpc-offer': [Tracker.optimizely],
  'purchase-tour-offer': [Tracker.optimizely],
  'save-to-trip': [Tracker.optimizely],
  'search-click-lpc-offer': [Tracker.optimizely],
  'search-click-bedbank-offer': [Tracker.optimizely],
  'search-map-click-offer': [Tracker.optimizely],
  'search-map-click-lpc-offer': [Tracker.optimizely],
  'search-map-click-flash-offer': [Tracker.optimizely],
  'search-map-click-bedbank-offer': [Tracker.optimizely],
  'search-map-pin-click-offer': [Tracker.optimizely],
  'search-map-pin-click-lpc-offer': [Tracker.optimizely],
  'search-map-pin-click-flash-offer': [Tracker.optimizely],
  'search-map-pin-click-bedbank-offer': [Tracker.optimizely],
  'search-click-offer': [Tracker.optimizely],
  'search-click-flash-offer': [Tracker.optimizely],
  'search-click-typeahead': [Tracker.optimizely],
  'search-click-typeahead-*': [Tracker.optimizely],
  'search-event': [Tracker.snowplow],
  'signup-success': [Tracker.optimizely],
  'upgrade-package': [Tracker.optimizely],
  'search-from-homepage': [Tracker.optimizely],
  'click-homepage-navbar': [Tracker.optimizely],
  'click-homepage-tile': [Tracker.optimizely],
  'click-offer-on-hotels-page': [Tracker.optimizely],
  'click-homepage-recommendation-tile': [Tracker.optimizely],
  'click-old-homepage-tile-including-recommendation': [Tracker.optimizely],
  checkout: [Tracker.optimizely],
  'view-map-page': [Tracker.optimizely],
  // to track about us click from the specific USP component (FrontPageUSPComponent)
  'usp-about-us-event': [Tracker.snowplow],

  // This is temporary and will be removed in the next few weeks as we aim to unify our analytics events
  'view-offer': [Tracker.optimizely],
  // Will be removed after experiment ends
  'signup-success-event': [Tracker.snowplow],
  'signin-success-event': [Tracker.snowplow],
  'tour-upgrade-package': [Tracker.snowplow],
  'click-tour-trending-destinations': [Tracker.snowplow],
  'view-recommendation-on-lpc-page': [Tracker.optimizely],
  'click-recommendation-on-lpc-page': [Tracker.optimizely],
  'share-offer': [Tracker.optimizely],
  'page-view': [Tracker.snowplow],
  'view-trip': [Tracker.optimizely],
  'view-all-carousel-homepage': [Tracker.snowplow],
  'view-tour-carousel-homepage': [Tracker.snowplow],
  'tour-event': [Tracker.snowplow],
  'purchase-click-au': [Tracker.optimizely],
  'click-offer-page-read-more': [Tracker.optimizely],
  'click-offer-gallery': [Tracker.optimizely],
  'click-recommendation-on-offer-page': [Tracker.optimizely],
  'view-recommendation-on-offer-page': [Tracker.optimizely],
  'interact-offer-gallery': [Tracker.optimizely],
  'click-offer-truncated-text': [Tracker.optimizely],
  'insurance-purchase': [Tracker.optimizely],
  'booking-protection-purchase': [Tracker.optimizely],
  'purchase-success': [Tracker.snowplow],
  'scroll-to-offer-package-options': [Tracker.optimizely],
  'click-trending-destinations-pill': [Tracker.optimizely],
  'click-walled-content-tile': [Tracker.optimizely],
  'click-top-pick-carousel-for-unloggin': [Tracker.optimizely],
  'view-top-pick-carousel-for-unloggin': [Tracker.optimizely],
  'time-spent-on-page': [Tracker.optimizely],
}

export async function track(eventName: keyof typeof eventSubscribers, event: OptimizelyEvent | SnowplowEvent) {
  if (!eventSubscribers[eventName]) {
    throw new Error(`Event ${eventName} not supported`)
  }

  if (eventSubscribers[eventName].includes(Tracker.optimizely)) {
    Optimizely.track(event as OptimizelyEvent)
  }

  if (eventSubscribers[eventName].includes(Tracker.snowplow)) {
    Snowplow.track(event as SnowplowEvent)
  }
}

export function init(store) {
  if (config.OPTIMIZELY_SDK_KEY) {
    Optimizely.init(store)
  }

  if (config.TRACKING_HOST) {
    Snowplow.init()
  }
}

export function resetContext() {
  Snowplow.resetContext()
}
